<template>
  <div
    v-if="!isDisabled"
    class="action"
  >
    <div class="action__left">
      <basic-text-button
        v-if="!$props.hideDetails"
        class="link"
        :label="$t('product.more')"
        @click="$emit('open-detail')"
      >
        <template #trailing>
          <the-icon
            art="solid"
            name="arrow-right"
          />
        </template>
      </basic-text-button>
    </div>

    <div class="action__right">
      <basic-button
        v-if="$props.product.selected && $props.isDirty"
        :label="$t('form.apply')"
        @click="emit('apply')"
      />

      <basic-button
        v-else-if="$props.productState === PRODUCT_STATE.EDITABLE && ($props.product.selected || $props.product.valid)"
        hierarchy="secondary"
        :label="title"
        @click="actionHandler"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'

  import useI18n from '@shared/hooks/useI18n'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'

  import { BasicButton, BasicTextButton } from '@shared/components/Basic'

  import { PRODUCT_STATE } from '@shared/config/constants'

  // HOOKS
  const { t, te } = useI18n()

  // INIT
  const emit = defineEmits(['add', 'apply', 'open-detail', 'search', 'remove'])
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    hasLoadingIndicator: {
      type: Boolean,
      default: false,
    },

    hideDetails: {
      type: Boolean,
      default: false,
    },

    isDirty: {
      type: Boolean,
      default: false,
    },

    personId: {
      type: String,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    productState: {
      type: String,
      required: true
    }
  })

  // COMPUTED
  const isDisabled = computed(() => {
    return props.disabled || (!props.product.valid && !props.product.selected)
  })

  /**
   * Only show search doctor if the product is not selected.
   * If it's selected and the doctor is missing,
   * a warning message and action will be displayed in the product doctor.
   */
  const needsDoctor = computed(() => {
    return props.product.medicalOffice && !props.product.doctor && !props.product.selected
  })

  const title = computed(() => {
    if (!props.product.valid && !props.product.selected) {
      return t('product.unavailable')
    } else if (props.product.selected) {
      return getContent(props.product.productId, 'actions.remove')
    } else {
      return getContent(props.product.productId, 'actions.add')
    }
  })

  const willReplaceIds = computed(() => {
    const { willReplace } = usePersonSpecificProduct()

    return willReplace(props.personId, props.product.productId)
  })

  // METHODS
  function actionHandler() {
    if (isDisabled.value) return
    if (props.product.medicalOffice && needsDoctor.value) {
      emit('search')
    } else if (!props.product.selected && props.product.valid) {
      const productId = willReplaceIds.value
      emit('add', { replace: !!productId, productId })
    } else if (props.product.selected) {
      emit('remove')
    }
  }

  function getContent(productId, key) {
    const productPath = `products.${productId}`
    return te(`${productPath}.${key}`) ? t(`${productPath}.${key}`) : t(`product.${key}`)
  }
</script>

<style scoped>
  .action {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    column-gap: var(--fixed-spacing-fix-02);
    row-gap: var(--fixed-spacing-fix-04);

    .action__left {
      text-align: left;
    }

    .action__right {
      text-align: right;
    }

    .button.plain {
      color: var(--c-primary-neutral-1) !important;

      .icon {
        color: var(--c-primary-neutral-1);
        margin: 0 0 0 5px;
      }
    }

    .action--disabled button,
    .action--disabled button:hover {
      cursor: not-allowed !important;
      border-color: transparent !important;
      background: none !important;
      color: var(--c-primary-color-3) !important;
      fill: var(--c-primary-color-3) !important;
    }

    .action__unavailable {
      flex-flow: row-reverse;
    }
  }

  @media (--v-wide) {
    .action {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  @media (--v-large) {
  }

  @media (--v-wide) {
  }

  @media (--v-ultra) {
  }

  @media (--v-max) {
  }
</style>
